// Which pieces are hurt, and how badly.
//
// **Damage had no picture.** It was a pip on a badge — real, but a pip says
// *there is damage here* and a fight wants *how much is left*, across a whole
// board, at a glance. A turret firing twice a round makes that the standing question:
// the interesting board state is the row of pieces one hit from dying, and
// reading it meant hovering each one in turn.
//
// Pure and separate from `pieceBadges` because the two answer different
// questions and must not be forced to agree on a subject. A badge is offered
// for a piece with *something to say*; a health bar is owed to every piece that
// has been hit, including ones already covered by a badge about something else
// entirely. Coupling them would mean a loaded rook's badge deciding whether its
// wounds are drawn.

import { pieceColor, type Color, type Piece } from "src/engine/rules/board";
import { healthOf, hurtAt, pieceAt, platingAt } from "src/engine/rules/position";
import { plateWorthMarking } from "./plateMark";
import type { GameState } from "src/engine/rules/commands";


/** One piece's wounds and its plate, ready to draw. */
export interface HealthBar {
  square: number;
  owner: Color;
  /**
   * How much of its health is left, 0..1. One only for a piece that is whole
   * *and* armoured — a whole, unarmoured piece has no bar.
   */
  fraction: number;
  /** What is left and what it started with, for the label a hover gives. */
  left: number;
  full: number;
  /**
   * Armour still to chew through (`Position.plating`), 0 for none. Drawn as its
   * own segment beside the health, because a turret grinds this first and a bar
   * that could not see it would understate how many shots the piece has left.
   */
  plate: number;
}

/**
 * Every piece carrying damage, or wearing armour.
 *
 * **Only partial health, or a plate worth marking** (worn or extra — every
 * knight and rook is born plated, §31 K1), which is the whole rule: an undamaged
 * army would otherwise wear thirty-two full bars, and a board where everything
 * is marked is a board where nothing is. So a bar appearing *is* the
 * information, and it disappears when the piece is healed. Armour is the one
 * thing that puts a bar on a whole piece: it is exactly "how much is left before
 * a shot reaches the body", which is the question the bar exists to answer.
 *
 * Sorted by square, because two clients render one board and an overlay list
 * in map order is one more thing that can differ between them for no reason.
 */
export function healthBars(state: GameState): HealthBar[] {
  const out: HealthBar[] = [];
  for (const [square, piece] of state.position.board) {
    const hurt = hurtAt(state.position, square);
    const plate = platingAt(state.position, square);
    // A whole piece's plate only when worn or extra (`plateWorthMarking`):
    // every knight and rook is born plated (§31 K1).
    if (hurt <= 0 && !plateWorthMarking(state.position, square)) continue;

    const left = healthOf(state.position, square);
    const full = left + hurt;
    // A piece whose full health is nought cannot be shown as a fraction of it,
    // and one exists: `PIECE_VALUE[KING]` is zero on purpose, so a *last* king
    // — the one thing that can never be hurt — would divide by it if the guard
    // above ever stopped holding. **A plated last king still gets its bar**,
    // the plate alone: the pip and the stats row both say it wears one, and
    // the three homes have to agree on every piece or the bar reads as "this
    // king is not armoured" against a card that says it is.
    if (full <= 0 && plate <= 0) continue;

    out.push({
      square,
      owner: pieceColor(piece as Piece),
      fraction: full > 0 ? Math.max(0, Math.min(1, left / full)) : 1,
      left,
      full,
      plate
    });
  }
  out.sort((a, b) => a.square - b.square);
  return out;
}

/**
 * Is this square worth drawing a bar on at all?
 *
 * Exposed so the overlay can ask about one square without building the list —
 * it projects per frame, and the answer decides whether a square is in the
 * projection set at all.
 */
export function isHurt(state: GameState, square: number): boolean {
  return pieceAt(state.position, square) !== 0 && hurtAt(state.position, square) > 0;
}
